/**
 * VideoPlayer — universal video playback component.
 *
 * Architecture:
 *   - Single <video> element always in DOM (never swapped/remounted)
 *   - Uses `src` attribute directly (NOT <source> children — they break with preload="none" in React)
 *   - preload="none" to avoid 16+ videos all loading simultaneously
 *   - Explicit .load() before .play() when readyState === 0
 *   - Native `poster` attribute for thumbnail
 *   - .play() called synchronously in user click handler (preserves gesture context)
 *   - isPlaying synced from actual video play/pause events
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Play, Pause, Maximize, Volume2, VolumeX } from 'lucide-react';
import ScrubBar from '@/components/shared/ScrubBar';

function formatTime(seconds) {
  if (!isFinite(seconds) || isNaN(seconds)) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export default function VideoPlayer({
  src,
  thumbnailUrl,
  duration: initialDuration = 0,
  compact = false,
}) {
  const videoRef = useRef(null);
  const hideTimerRef = useRef(null);

  const [started, setStarted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(initialDuration || 0);
  const [muted, setMuted] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [playbackRate, setPlaybackRate] = useState(1);

  const maxH = compact ? 'max-h-[200px]' : 'max-h-[400px]';

  // ── Auto-hide controls after 3s of playback ──
  const resetHideTimer = useCallback(() => {
    setShowControls(true);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
  }, []);

  useEffect(() => {
    if (started && isPlaying) {
      hideTimerRef.current = setTimeout(() => setShowControls(false), 3000);
    }
    return () => { if (hideTimerRef.current) clearTimeout(hideTimerRef.current); };
  }, [started, isPlaying, showControls]);

  // ── Sync isPlaying with actual video element state ──
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    v.addEventListener('play', onPlay);
    v.addEventListener('pause', onPause);
    return () => { v.removeEventListener('play', onPlay); v.removeEventListener('pause', onPause); };
  }, []);

  const handleDurationAvailable = useCallback(() => {
    if (videoRef.current && isFinite(videoRef.current.duration) && videoRef.current.duration > 0) {
      setDuration(videoRef.current.duration);
    }
  }, []);

  const handleEnded = useCallback(() => {
    setIsPlaying(false);
    setShowControls(true);
  }, []);

  // ── First play — synchronous in user gesture ──
  const handleStart = useCallback((e) => {
    e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    setStarted(true);
    setShowControls(true);
    setLoadError(null);

    // With preload="none", the video has no data. Kick off loading.
    v.load();

    // Attempt play — may fail if data hasn't arrived yet.
    v.play().catch((err) => {
      console.warn('VideoPlayer: initial play() failed, waiting for canplay...', err.name, err.message);
      // Wait for enough data to arrive, then retry play.
      const retry = () => {
        v.play().catch((e2) => {
          console.error('VideoPlayer: play() retry failed:', e2.name, e2.message);
          setLoadError('Unable to play video. Tap to retry.');
        });
      };
      v.addEventListener('canplay', retry, { once: true });
      // Safety timeout: if canplay never fires within 10s, show error
      setTimeout(() => {
        if (v.paused && v.readyState < 3) {
          v.removeEventListener('canplay', retry);
          setLoadError('Video loading timed out. Tap to retry.');
        }
      }, 10000);
    });
  }, []);

  // ── Toggle play/pause after first play ──
  const togglePlay = useCallback((e) => {
    if (e) e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      if (v.readyState === 0) v.load();
      v.play().catch(() => {});
    } else {
      v.pause();
    }
    resetHideTimer();
  }, [resetHideTimer]);

  const handleSeek = useCallback((time) => {
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      setCurrentTime(time);
    }
  }, []);

  const toggleMute = useCallback((e) => {
    if (e) e.stopPropagation();
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setMuted(m => !m);
    }
  }, []);

  const handleFullscreen = useCallback((e) => {
    if (e) e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    if (v.requestFullscreen) v.requestFullscreen();
    else if (v.webkitEnterFullscreen) v.webkitEnterFullscreen();
  }, []);

  const cycleRate = useCallback((e) => {
    if (e) e.stopPropagation();
    const rates = [1, 1.5, 2];
    const next = rates[(rates.indexOf(playbackRate) + 1) % rates.length];
    setPlaybackRate(next);
    if (videoRef.current) videoRef.current.playbackRate = next;
  }, [playbackRate]);

  const handleContainerClick = useCallback(() => {
    if (!started) return;
    togglePlay();
  }, [started, togglePlay]);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div
      className={`relative rounded-xl overflow-hidden ${maxH} bg-black`}
      onClick={handleContainerClick}
      onPointerMove={started ? resetHideTimer : undefined}
      data-testid="video-player"
    >
      {/* Single video element — src directly on video, NOT via <source> child */}
      <video
        ref={videoRef}
        src={src}
        poster={thumbnailUrl || undefined}
        className={`w-full ${maxH} ${started ? 'object-contain' : 'object-cover'}`}
        onTimeUpdate={() => videoRef.current && setCurrentTime(videoRef.current.currentTime)}
        onLoadedMetadata={handleDurationAvailable}
        onDurationChange={handleDurationAvailable}
        onEnded={handleEnded}
        onError={(e) => {
          const mediaErr = e.target.error;
          if (mediaErr) {
            const codes = { 1: 'ABORTED', 2: 'NETWORK', 3: 'DECODE', 4: 'SRC_NOT_SUPPORTED' };
            console.error('VideoPlayer: media error', codes[mediaErr.code] || mediaErr.code, mediaErr.message);
            if (started) setLoadError(`Video error: ${codes[mediaErr.code] || 'unknown'}. Tap to retry.`);
          }
        }}
        playsInline
        preload="none"
      />

      {/* Poster click-to-play overlay */}
      {!started && (
        <div
          className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/30 transition-colors cursor-pointer"
          onClick={handleStart}
          data-testid="video-player-start"
        >
          <div className="w-14 h-14 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center border border-white/20 hover:scale-110 transition-transform">
            <Play className="w-7 h-7 text-white ml-1" />
          </div>

          {(initialDuration > 0 || duration > 0) && (
            <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded font-medium">
              {formatTime(initialDuration || duration)}
            </div>
          )}
        </div>
      )}

      {/* Custom controls */}
      {started && (
        <div
          className={`absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent pt-10 pb-2 px-3 transition-opacity duration-200 ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          onClick={(e) => e.stopPropagation()}
        >
          <ScrubBar
            progress={progress}
            duration={duration}
            currentTime={currentTime}
            onSeek={handleSeek}
            colors={{ trackBg: 'bg-white/20', fillBg: 'bg-white', thumbBg: 'bg-white' }}
          />
          <div className="flex items-center justify-between -mt-1">
            <div className="flex items-center gap-2">
              <button onClick={togglePlay} className="text-white hover:text-white/80 transition-colors" data-testid="video-player-play-btn">
                {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
              </button>
              <span className="text-[11px] text-white/80 font-medium">
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={cycleRate} className="text-[10px] font-semibold text-white/70 hover:text-white bg-white/15 hover:bg-white/25 px-1.5 py-0.5 rounded transition-colors" data-testid="video-player-speed-btn">
                {playbackRate}x
              </button>
              <button onClick={toggleMute} className="text-white/80 hover:text-white transition-colors" data-testid="video-player-mute-btn">
                {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              </button>
              <button onClick={handleFullscreen} className="text-white/80 hover:text-white transition-colors" data-testid="video-player-fullscreen-btn">
                <Maximize className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Center play icon when paused */}
      {started && !isPlaying && showControls && !loadError && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-14 h-14 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center">
            <Play className="w-7 h-7 text-white ml-1" />
          </div>
        </div>
      )}

      {/* Error / retry overlay */}
      {loadError && (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center bg-black/60 cursor-pointer"
          onClick={(e) => { e.stopPropagation(); setLoadError(null); handleStart(e); }}
        >
          <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mb-2">
            <Play className="w-7 h-7 text-white ml-1" />
          </div>
          <p className="text-white/80 text-xs">{loadError}</p>
        </div>
      )}
    </div>
  );
}
